import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Storage,
  DataObject,
  ExpandLess,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { ProcessVariable } from '../types/process';

interface ActivityVariablesProps {
  /** Lista de variáveis da atividade */
  variables: ProcessVariable[];
  /** ID da atividade */
  activityId: string;
  /** Nome da atividade */
  activityName: string;
  /** Se deve mostrar detalhes por padrão */
  defaultExpanded?: boolean;
}

const ActivityVariables: React.FC<ActivityVariablesProps> = ({
  variables,
  activityName,
  defaultExpanded = false,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [detailedVariables, setDetailedVariables] = useState<Set<string>>(new Set());

  const toggleDetailedView = (variableId: string) => {
    const newDetailed = new Set(detailedVariables);
    if (newDetailed.has(variableId)) {
      newDetailed.delete(variableId);
    } else {
      newDetailed.add(variableId);
    }
    setDetailedVariables(newDetailed);
  };

  const formatValue = (value: unknown, type: string): string => {
    if (value === null || value === undefined) {
      return 'null';
    }

    if (type === 'Json') {
      try {
        return JSON.stringify(JSON.parse(String(value)), null, 2);
      } catch {
        return String(value);
      }
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }

    return String(value);
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'default' => {
    switch (type) {
      case 'Json':
        return 'primary';
      case 'String':
        return 'success';
      case 'Boolean':
        return 'warning';
      case 'Integer':
      case 'Long':
      case 'Double':
        return 'info';
      case 'Date':
        return 'secondary';
      default:
        return 'default';
    }
  };

  if (variables.length === 0) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Storage color="action" />
              <Typography variant="h6">
                Variáveis da Atividade: {activityName}
              </Typography>
            </Box>
          }
          subheader="Nenhuma variável encontrada para esta atividade"
        />
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Storage color="primary" />
            <Typography variant="h6">
              Variáveis da Atividade: {activityName}
            </Typography>
            <Chip 
              label={`${variables.length} variável${variables.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        }
        action={
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Recolher' : 'Expandir'}
          >
            {expanded ? <ExpandLess /> : <ExpandMoreIcon />}
          </IconButton>
        }
      />

      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Data de Criação</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variables.map((variable) => (
                  <React.Fragment key={variable.id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {variable.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={variable.type}
                          size="small"
                          color={getTypeColor(variable.type)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {detailedVariables.has(variable.id)
                            ? formatValue(variable.value, variable.type)
                            : String(variable.value).substring(0, 50) + (String(variable.value).length > 50 ? '...' : '')
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={variable.state}
                          size="small"
                          color={variable.state === 'CREATED' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(variable.create_time).toLocaleString('pt-BR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={detailedVariables.has(variable.id) ? 'Ocultar detalhes' : 'Mostrar detalhes'}>
                          <IconButton
                            size="small"
                            onClick={() => toggleDetailedView(variable.id)}
                          >
                            {detailedVariables.has(variable.id) ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                    {detailedVariables.has(variable.id) && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, px: 2 }}>
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              backgroundColor: 'grey.50',
                              border: '1px solid',
                              borderColor: 'grey.200',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                              <DataObject color="action" fontSize="small" />
                              <Typography variant="subtitle2" color="text.secondary">
                                Valor Completo ({variable.type})
                              </Typography>
                            </Box>
                            <Box
                              component="pre"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                backgroundColor: 'white',
                                p: 2,
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'grey.300',
                                overflow: 'auto',
                                maxHeight: 200,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {formatValue(variable.value, variable.type)}
                            </Box>
                          </Paper>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ActivityVariables;
